import { useCallback, useEffect, useState } from "react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerApi } from "@/lib/customerApi";
import { formatApiError } from "@/lib/api";

export default function AccountProfile() {
  const { customer, refresh } = useCustomerAuth();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState(customer?.name || "");
  const [changingEmail, setChangingEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await customerApi.get("/customers/profile");
      if (typeof data?.email !== "string" || typeof data?.password_enabled !== "boolean") throw new Error("Profile details could not be loaded.");
      setProfile(data);
      setName(data.name);
    } catch (err) { setError(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (event) => {
    event.preventDefault(); setBusy("name"); setError(""); setNotice("");
    try {
      const {data} = await customerApi.patch("/customers/profile", {name:name.trim()});
      setProfile(previous => ({...previous,name:data.name}));
      await refresh(); setNotice("Your name has been updated.");
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(""); }
  };
  const changeEmail = async (event) => {
    event.preventDefault(); setBusy("email"); setError(""); setNotice("");
    try {
      await customerApi.post("/customers/email-change", {email,current_password:password});
      setNotice(`Check ${email} for a verification link. Your current email stays active until you confirm.`);
      setPassword(""); setChangingEmail(false);
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(""); }
  };
  const resetPassword = async () => {
    setBusy("password"); setError(""); setNotice("");
    try {
      await customerApi.post("/customers/password/request-reset", {email:profile.email});
      setNotice("A password reset has been requested. If this account is eligible, check your inbox for the link.");
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(""); }
  };
  return <section id="account-profile" data-testid="account-profile" className="mt-10 border border-ink-100 bg-white p-7" aria-labelledby="account-profile-heading">
    <h2 id="account-profile-heading" className="font-display text-xl font-bold">Profile &amp; security</h2>
    {!profile ? <><p className="mt-3 text-sm text-ink-600">{error || "Loading your account details…"}</p>{error ? <button className="lf-link mt-3" type="button" onClick={load}>Retry profile</button> : null}</> : <div className="mt-5 space-y-6">
      <form onSubmit={save} className="space-y-3">
        <label className="block text-sm font-semibold">Name
          <input className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm" autoComplete="name" value={name} maxLength={160} required onChange={e=>setName(e.target.value)} data-testid="profile-name" />
        </label>
        <button className="lf-btn-secondary" type="submit" disabled={Boolean(busy)||!name.trim()||name.trim()===profile.name}>{busy==="name" ? "Saving…" : "Save name"}</button>
      </form>
      <div className="border-t border-ink-100 pt-5">
        <h3 className="text-sm font-semibold">Email</h3>
        <p className="mt-2 break-words text-sm text-ink-600" data-testid="profile-email">{profile.email}</p>
        <button className="lf-link mt-3" type="button" disabled={Boolean(busy)} aria-expanded={changingEmail} onClick={()=>{setChangingEmail(v=>!v);setPassword("");setError("");}}>{changingEmail ? "Cancel email change" : "Change email"}</button>
        {changingEmail ? <form onSubmit={changeEmail} className="mt-4 space-y-3">
          <label className="block text-sm font-semibold">New email
            <input className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm" type="email" autoComplete="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="profile-new-email" />
          </label>
          {profile.password_enabled ? <label className="block text-sm font-semibold">Current password
            <input className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm" type="password" autoComplete="current-password" required value={password} onChange={e=>setPassword(e.target.value)} data-testid="profile-current-password" />
          </label> : <p className="text-sm text-ink-600">Sign in again with your linked provider before changing your email.</p>}
          <p className="text-xs text-ink-500">We'll verify the new address before updating your account. Your products and purchases stay with this account.</p>
          <button className="lf-btn-secondary" type="submit" disabled={Boolean(busy)}>{busy==="email" ? "Sending…" : "Send verification link"}</button>
        </form> : null}
      </div>
      <div className="border-t border-ink-100 pt-5">
        <h3 className="text-sm font-semibold">Password</h3>
        {profile.password_enabled ? <button className="lf-link mt-3" type="button" disabled={Boolean(busy)} onClick={resetPassword}>{busy==="password" ? "Requesting…" : "Send password reset link"}</button> : <p className="mt-2 text-sm text-ink-600">You sign in with {profile.sign_in_methods?.join(", ") || "a linked provider"}. Manage that password with your sign-in provider.</p>}
      </div>
    </div>}
    {notice ? <p className="mt-4 text-sm text-green-700" role="status">{notice}</p> : null}
    {profile && error ? <p className="mt-4 text-sm text-red-600" role="alert">{error}</p> : null}
  </section>;
}
